import { createProtocolOrderCartMutation } from '@/api/carts/adminPatientCartMutations';
import { createCartMutation } from '@/api/carts/cartMutations';
import type { CartResponse } from '@/api/carts/cartQueries';
import { queryKeys } from '@/api/queryKeys';
import { getUsersQuery } from '@/api/users/usersQueries';
import { Button } from '@/components/common/Button';
import { Combobox } from '@/components/common/Combobox';
import { Card, CardContent } from '@/components/dashboard/DashboardCard';
import { Role } from '@/domain/Role';
import { useHasRole } from '@/hooks/useHasRole';
import { routes } from '@/routes/routes';
import { protocolOrderSchema } from '@/validation/protocolOrderValidator';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useFormik } from 'formik';
import { toast } from 'sonner';
import type * as z from 'zod';
import { toFormikValidate } from 'zod-formik-adapter';

type ProtocolOrderFormValues = z.infer<typeof protocolOrderSchema>;

export interface SelectedProtocolItem {
  protocolTypeItemId: number;
  itemId: number;
  priceId?: number;
}

interface ProtocolOrderFormCardProps {
  completeProtocolPriceCents: number;
  selectedSupplementsPriceCents: number;
  onAddCompleteProtocol: () => void;
  selectedItems: Array<SelectedProtocolItem>;
}

export function ProtocolOrderFormCard({
  completeProtocolPriceCents,
  selectedSupplementsPriceCents,
  onAddCompleteProtocol,
  selectedItems,
}: ProtocolOrderFormCardProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasRole } = useHasRole();
  const canSelectPatient = hasRole(
    Role.DOCTOR,
    Role.SHOP_ADMIN,
    Role.SUPERADMIN,
  );
  const { data: usersResponse } = useQuery({
    ...getUsersQuery(1, { per_page: 10000 }),
    enabled: canSelectPatient,
  });
  const navigateToCart = (cartId: number, to: string) => {
    navigate({ to, params: { cartId } });
  };

  const adminOrderMutation = useMutation({
    ...createProtocolOrderCartMutation(selectedItems),
    onSuccess: (cart) => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.orders.index] });
      toast.success('Cart created successfully');
      navigateToCart(cart.id, routes.dashboard.subscription.cart.items);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create cart');
    },
  });

  const itemsWithPrice = selectedItems.filter(
    (s): s is SelectedProtocolItem & { priceId: number } =>
      s.priceId != null && s.priceId > 0,
  );
  const userOrderMutation = useMutation({
    ...createCartMutation(),
    onSuccess: (res: CartResponse) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.carts.all() });
      toast.success('Cart created successfully');
      navigateToCart(res.data.id, routes.dashboard.myCarts.cart.items);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create cart');
    },
  });

  const orderMutation = canSelectPatient
    ? adminOrderMutation
    : userOrderMutation;

  const formik = useFormik<ProtocolOrderFormValues>({
    initialValues: { patientId: 0 },
    validate: canSelectPatient
      ? toFormikValidate(protocolOrderSchema)
      : () => ({}),
    onSubmit: (values) => {
      const uniqueName = `Protocol order ${Date.now()}`;
      if (canSelectPatient) {
        adminOrderMutation.mutate({
          user_id: values.patientId,
          name: uniqueName,
        });
      } else {
        userOrderMutation.mutate({
          name: uniqueName,
          items: itemsWithPrice.map((s) => ({
            price_id: s.priceId,
            quantity: 1,
          })),
        });
      }
    },
  });

  const users = usersResponse?.data ?? [];
  const patientItems = users.map((p) => ({
    id: p.id,
    value: String(p.id),
    label: `${p.name} ${p.surname}`,
  }));

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span>Complete protocol price</span>
            <span>{(completeProtocolPriceCents / 100).toFixed(2)} €</span>
          </div>

          <div className="flex items-center justify-between">
            <span>Selected supplements price</span>
            <span>{(selectedSupplementsPriceCents / 100).toFixed(2)} €</span>
          </div>
        </div>

        {canSelectPatient && (
          <Combobox
            id="patient"
            label="Select patient"
            items={patientItems}
            value={
              formik.values.patientId > 0 ? formik.values.patientId : undefined
            }
            onChange={(value) => {
              formik.setFieldValue('patientId', value ? Number(value) : 0);
            }}
            onBlur={() => formik.setFieldTouched('patientId', true)}
            error={
              formik.touched.patientId && formik.errors.patientId
                ? formik.errors.patientId
                : undefined
            }
            touched={!!formik.touched.patientId}
            emptyText="No patients found."
          />
        )}

        <div className="flex max-w-62 flex-col justify-between gap-3 md:max-w-none md:flex-row md:items-center">
          <Button
            variant="outlinePrimary"
            className="h-15"
            onClick={onAddCompleteProtocol}
          >
            Add complete protocol
          </Button>
          <Button
            variant="secondary"
            className="h-15"
            onClick={() => formik.handleSubmit()}
            disabled={
              orderMutation.isPending ||
              selectedItems.filter((s) => s.priceId).length === 0
            }
          >
            {orderMutation.isPending ? 'Creating...' : 'Review order →'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
